import { useEffect, useState } from "react";
import {
  Users, Send, CheckCheck, Eye, XCircle, Clock, TrendingUp, FileText, Gauge,
  Mail, Save, Loader2, AlertTriangle, Target,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { WebhookStatusBadge } from "@/components/WebhookStatusBadge";
import { BrandLogoCard } from "@/components/BrandLogoCard";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const Card = ({ children, className = "", ...rest }) => (
  <div
    className={`bg-[#111827]/80 backdrop-blur-md border border-[#1F2937] rounded-2xl p-6 ${className}`}
    {...rest}
  >
    {children}
  </div>
);

const Delta = ({ d, invert }) => {
  if (!d || d.delta_pct === null || d.delta_pct === undefined) return null;
  const good = invert ? d.delta_pct <= 0 : d.delta_pct >= 0;
  return (
    <span className={`ml-1.5 text-xs font-semibold ${good ? "text-[#10B981]" : "text-[#F87171]"}`} title={`Mese scorso: ${d.previous}`}>
      {d.delta_pct > 0 ? "▲" : d.delta_pct < 0 ? "▼" : "="}{Math.abs(d.delta_pct)}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, color = "#10B981", testid, delta, invert }) => (
  <Card className="hover:border-[#10B981]/40 transition-all duration-200" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
        <p className="font-display text-3xl font-extrabold text-slate-100 mt-2">{value}{delta && <Delta d={delta} invert={invert} />}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </div>
      <div
        className="h-11 w-11 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}1a` }}
      >
        <Icon className="h-6 w-6" style={{ color }} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [qhist, setQhist] = useState([]);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);

  const load = () => {
    setError(null);
    api.get("/dashboard/stats", { timeout: 45000 }).then((r) => setStats(r.data))
      .catch((e) => setError(e?.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : "Il server non risponde. Riprova tra qualche secondo."));
    api.get("/quality/history").then((r) => setQhist(r.data || [])).catch(() => {});
    Promise.allSettled([api.get("/whatsapp/status"), api.get("/app-settings")]).then(([w, s]) => setMode({
      wa: w.status === "fulfilled" ? !!w.value.data?.configured : false,
      smsSim: s.status === "fulfilled" ? !!s.value.data?.sms_simulate : false,
    }));
  };

  useEffect(() => { load(); }, []);

  if (!stats)
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4" data-testid="dashboard-loading">
        {error ? (
          <>
            <AlertTriangle className="h-8 w-8 text-[#F59E0B]" />
            <p className="text-sm text-slate-400 text-center max-w-sm" data-testid="dashboard-error">{error}</p>
            <Button onClick={load} data-testid="dashboard-retry-button" className="bg-[#10B981] text-[#0A0E17] hover:bg-[#0ea371]">Riprova</Button>
          </>
        ) : (
          <div className="h-10 w-10 rounded-full border-2 border-[#10B981] border-t-transparent animate-spin" />
        )}
      </div>
    );

  const { quota, contacts, delivery, campaigns, trend, templates, budget } = stats;
  const qdata = qhist.map((h) => ({
    t: `${new Date(h.at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} ${new Date(h.at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`,
    rank: h.rank, rating: h.rating,
  }));

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Panoramica delle tue comunicazioni WhatsApp</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <WebhookStatusBadge />
          {mode && (mode.wa
            ? <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-xs text-[#10B981] font-medium" data-testid="mode-badge" title="WhatsApp Business collegato: i messaggi partono realmente">
                <span className="h-2 w-2 rounded-full bg-[#10B981] animate-pulse" /> WhatsApp operativo{mode.smsSim ? " · SMS in simulazione" : ""}
              </span>
            : <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-xs text-[#F59E0B] font-medium" data-testid="mode-badge" title="WhatsApp non collegato: nessun messaggio reale viene inviato">
                <span className="h-2 w-2 rounded-full bg-[#F59E0B]" /> WhatsApp non collegato (anteprima)
              </span>)}
        </div>
      </div>

      {budget && budget.exceeded && (
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="budget-alert-banner">
          <AlertTriangle className="h-5 w-5 text-[#EF4444] shrink-0" />
          <p className="text-sm text-[#FCA5A5]">
            Budget superato: la spesa stimata del mese è <strong>€ {budget.month_cost.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</strong>, oltre la soglia di <strong>€ {budget.threshold.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</strong> ({budget.percent}%). Imposta o modifica la soglia nella sezione Report & Costi.
          </p>
        </div>
      )}

      {/* Quota banner */}
      <Card data-testid="quota-card" className="bg-gradient-to-br from-[#111827] to-[#0B1120]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-[#10B981]/15 flex items-center justify-center">
              <Gauge className="h-7 w-7 text-[#10B981]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Quota contatti del mese</p>
              <p className="font-display text-2xl font-extrabold text-slate-100">
                {quota.used.toLocaleString("it-IT")}{" "}
                <span className="text-slate-500 text-lg">/ {quota.limit.toLocaleString("it-IT")}</span>
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-mono2 text-[#10B981] text-lg">{quota.remaining.toLocaleString("it-IT")}</p>
            <p className="text-xs text-slate-500">rimanenti</p>
          </div>
        </div>
        <Progress value={quota.percent} className="h-3 mt-4 bg-[#1F2937]" />
        {stats.daily && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1F2937]" data-testid="daily-limit-row">
            <p className="text-xs text-slate-400">
              Invii reali oggi:{" "}
              <span className="text-slate-100 font-mono2">{stats.daily.used.toLocaleString("it-IT")}</span>
              {stats.daily.unlimited
                ? <span className="text-slate-500"> / illimitato</span>
                : <span className="text-slate-500"> / {stats.daily.limit.toLocaleString("it-IT")} al giorno</span>}
            </p>
            {!stats.daily.unlimited && (
              <p className="text-xs font-mono2 text-[#10B981]">
                {stats.daily.remaining.toLocaleString("it-IT")} rimanenti oggi
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Obiettivo mensile cliente */}
      {stats.target && (
        <Card data-testid="target-card" className="bg-gradient-to-br from-[#111827] to-[#0B1120]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center">
                <Target className="h-7 w-7 text-[#F59E0B]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Messaggi inviati nel mese</p>
                <p className="font-display text-2xl font-extrabold text-slate-100" data-testid="target-counter">
                  {stats.target.sent.toLocaleString("it-IT")}{" "}
                  <span className="text-slate-500 text-lg">/ {stats.target.target.toLocaleString("it-IT")}</span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono2 text-[#10B981] text-lg" data-testid="target-cost">€ {stats.target.month_cost.toLocaleString("it-IT", { minimumFractionDigits: 2 })}<Delta d={stats.compare?.cost} invert /></p>
              <p className="text-xs text-slate-500">spesa Meta finora ({stats.target.cost_source === "meta" ? "reale" : "stima"})</p>
            </div>
          </div>
          <div className="relative mt-4">
            <Progress value={Math.min(100, stats.target.percent)} className="h-3 bg-[#1F2937]" />
            {stats.target.projected_percent > stats.target.percent && (
              <div className="absolute top-0 h-3 rounded-r-full bg-[#F59E0B]/40 pointer-events-none"
                   style={{ left: `${Math.min(100, stats.target.percent)}%`, width: `${Math.max(0, Math.min(100, stats.target.projected_percent) - Math.min(100, stats.target.percent))}%` }} />
            )}
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2 text-xs" data-testid="target-projection">
            <p className="text-slate-400">
              Proiezione fine mese: <span className={`font-mono2 ${stats.target.over_target ? "text-[#EF4444]" : "text-slate-100"}`}>{stats.target.projected_messages.toLocaleString("it-IT")}</span> messaggi
              <span className="text-slate-500"> ({stats.target.projected_percent}%)</span> · spesa prevista <span className="font-mono2 text-[#F59E0B]">€ {stats.target.projected_cost.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
              {stats.target.unit_cost ? <span className="text-slate-500"> · media € {stats.target.unit_cost.toLocaleString("it-IT", { minimumFractionDigits: 4 })}/msg</span> : null}
            </p>
            <p className="text-slate-500">
              {stats.target.scheduled_campaigns > 0
                ? `${stats.target.scheduled_messages.toLocaleString("it-IT")} messaggi in ${stats.target.scheduled_campaigns} campagn${stats.target.scheduled_campaigns === 1 ? "a programmata" : "e programmate"} entro il mese`
                : "Nessuna campagna reale programmata entro il mese"} · giorno {stats.target.days_elapsed}/{stats.target.days_in_month}
            </p>
          </div>
          {stats.target.over_target && (
            <p className="mt-2 text-xs text-[#EF4444]" data-testid="target-over-warning">La proiezione supera l'obiettivo mensile di {(stats.target.projected_messages - stats.target.target).toLocaleString("it-IT")} messaggi.</p>
          )}
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi icon={Users} label="Contatti totali" value={contacts.total.toLocaleString("it-IT")}
             sub={`${contacts.subscribed} iscritti · ${contacts.unsubscribed} disiscritti`} testid="stats-contacts" />
        <Kpi icon={Send} label="Messaggi inviati" value={delivery.sent.toLocaleString("it-IT")}
             sub="questo mese" color="#3B82F6" testid="stats-sent-count" delta={stats.compare?.sent} />
        <Kpi icon={CheckCheck} label="Tasso consegna" value={`${delivery.delivery_rate}%`}
             sub={`${delivery.delivered.toLocaleString("it-IT")} consegnati`} color="#10B981" testid="stats-delivered-count" delta={stats.compare?.delivered} />
        <Kpi icon={Eye} label="Tasso lettura" value={`${delivery.read_rate}%`}
             sub={`${delivery.read.toLocaleString("it-IT")} letti`} color="#8B5CF6" testid="stats-read-count" delta={stats.compare?.read} />
      </div>
      {stats.compare && (
        <p className="text-xs text-slate-500 -mt-3" data-testid="dashboard-compare-note">
          Frecce = variazione rispetto al mese scorso · Clic sui link: <span className="text-slate-300">{stats.compare.clicks.current}</span><Delta d={stats.compare.clicks} /> · Costo: <span className="text-slate-300">€ {stats.compare.cost.current.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span><Delta d={stats.compare.cost} invert />
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Trend chart */}
        <Card className="lg:col-span-8" data-testid="trend-chart-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-[#10B981]" />
            <h3 className="font-display text-lg font-semibold text-slate-200">Andamento invii e costi (7 giorni)</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend} margin={{ left: -20, right: -10, top: 10 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="day" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis yAxisId="left" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis yAxisId="cost" orientation="right" stroke="#F59E0B" fontSize={12} tickLine={false} axisLine={false}
                     tickFormatter={(v) => `€${v}`} />
              <Tooltip
                contentStyle={{ background: "#0B1120", border: "1px solid #1F2937", borderRadius: 12, color: "#F8FAFC" }}
                formatter={(val, name) => name === "costo" ? [`€ ${Number(val).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`, "Costo stimato"] : [val, "Messaggi"]}
              />
              <Area yAxisId="left" type="monotone" dataKey="messaggi" stroke="#10B981" strokeWidth={2.5} fill="url(#g)" />
              <Area yAxisId="cost" type="monotone" dataKey="costo" stroke="#F59E0B" strokeWidth={2.5} fill="url(#gc)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Quality history chart */}
        <Card className="lg:col-span-8" data-testid="quality-chart-card">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="h-5 w-5 text-[#10B981]" />
            <h3 className="font-display text-lg font-semibold text-slate-200">Andamento qualità numero WhatsApp</h3>
          </div>
          {qdata.length === 0 ? (
            <p className="text-sm text-slate-500 py-10 text-center" data-testid="quality-chart-empty">Nessun dato di qualità ancora registrato. Viene raccolto automaticamente ogni 6 ore.</p>
          ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={qdata} margin={{ left: -10, right: -10, top: 10 }}>
              <defs>
                <linearGradient id="gq" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="t" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 3]} ticks={[1, 2, 3]} stroke="#64748B" fontSize={11} tickLine={false} axisLine={false}
                     width={62} tickFormatter={(v) => ({ 1: "RED", 2: "YELLOW", 3: "GREEN" }[v] || "")} />
              <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #1F2937", borderRadius: 12, color: "#F8FAFC" }}
                       formatter={(v, n, p) => [p.payload.rating, "Qualità"]} />
              <Area type="stepAfter" dataKey="rank" stroke="#10B981" strokeWidth={2.5} fill="url(#gq)" />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </Card>

        {/* Side stats */}
        <div className="lg:col-span-4 space-y-6">
          <Card>
            <h3 className="font-display text-lg font-semibold text-slate-200 mb-4">Qualità invii</h3>
            <div className="space-y-4">
              <Row icon={CheckCheck} color="#10B981" label="Consegnati" value={delivery.delivered} />
              <Row icon={Eye} color="#8B5CF6" label="Letti" value={delivery.read} />
              <Row icon={XCircle} color="#EF4444" label="Falliti" value={delivery.failed} sub={`${delivery.fail_rate}%`} />
            </div>
          </Card>
          <div className="grid grid-cols-2 gap-4">
            <Card className="text-center">
              <Clock className="h-6 w-6 text-[#F59E0B] mx-auto mb-2" />
              <p className="font-display text-2xl font-extrabold text-slate-100">{campaigns.scheduled}</p>
              <p className="text-xs text-slate-500">programmate</p>
            </Card>
            <Card className="text-center">
              <FileText className="h-6 w-6 text-[#3B82F6] mx-auto mb-2" />
              <p className="font-display text-2xl font-extrabold text-slate-100">{templates}</p>
              <p className="text-xs text-slate-500">template</p>
            </Card>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8"><ReportSettingsCard /></div>
        <div className="lg:col-span-4"><BrandLogoCard /></div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, color, label, value, sub }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5" style={{ color }} />
      <span className="text-sm text-slate-300">{label}</span>
    </div>
    <div className="text-right">
      <span className="font-mono2 text-slate-100">{value.toLocaleString("it-IT")}</span>
      {sub && <span className="text-xs text-slate-500 ml-2">{sub}</span>}
    </div>
  </div>
);

const WEEKDAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

const ReportSettingsCard = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        report_enabled: s.report_enabled,
        report_weekday: s.report_weekday,
        report_hour: s.report_hour,
      });
      setS(data);
      toast.success("Impostazioni report salvate");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/reports/send-now");
      toast.success("Report inviato via email all'admin");
    } catch {
      toast.error("Errore durante l'invio del report");
    } finally {
      setSending(false);
    }
  };

  if (!s) return null;

  return (
    <div className="bg-[#111827]/80 backdrop-blur-md border border-[#1F2937] rounded-2xl p-6" data-testid="report-settings-card">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#3B82F6]/15 flex items-center justify-center">
            <Mail className="h-6 w-6 text-[#3B82F6]" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-200">Report settimanale via email</h3>
            <p className="text-xs text-slate-500">Inviato automaticamente all'admin con il CSV in allegato</p>
          </div>
        </div>
        <Button onClick={sendNow} disabled={sending} data-testid="send-report-now-button"
                className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Invia report ora
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="flex items-center gap-3">
          <Switch checked={s.report_enabled} data-testid="report-enabled-switch"
                  onCheckedChange={(v) => setS({ ...s, report_enabled: v })} />
          <span className="text-sm text-slate-300">{s.report_enabled ? "Attivo" : "Disattivato"}</span>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Giorno</label>
          <Select value={String(s.report_weekday)} onValueChange={(v) => setS({ ...s, report_weekday: parseInt(v, 10) })}>
            <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] text-slate-100" data-testid="report-weekday-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
              {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Ora (UTC)</label>
          <Select value={String(s.report_hour)} onValueChange={(v) => setS({ ...s, report_hour: parseInt(v, 10) })}>
            <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] text-slate-100" data-testid="report-hour-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-h-60">
              {Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={save} disabled={saving} variant="outline" data-testid="save-report-settings-button"
                  className="w-full bg-[#1E293B] border-[#10B981]/20 text-[#10B981] hover:bg-[#334155]">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva
          </Button>
        </div>
      </div>
    </div>
  );
};
